import { useMemo } from 'react';

import ProgressBar from '@ramonak/react-progress-bar';
import { useTranslation } from 'react-i18next';

import { Chip } from '../../components/chip';
import { Skeleton } from '../../components/skeleton';
import { SubTitle } from '../../components/subtitle';
import { cn } from '../../lib/utils';
import { OrganisationUsage } from '@/api/types/backend-dto-types';
import { TooltipIcon } from '@/new/components/tooltip-icon';

export function CreditUsage({
  periodCreditUsage,
  isLoading,
}: {
  periodCreditUsage: OrganisationUsage['periodCreditUsage'];
  isLoading: boolean;
}) {
  const { i18n, t } = useTranslation();

  const totalCreditsUsed = useMemo(() => {
    if (!periodCreditUsage) return 0;
    return periodCreditUsage.used;
  }, [periodCreditUsage]);

  const usagePercentage = useMemo(() => {
    if (!periodCreditUsage) return 0;
    const total = periodCreditUsage.maxCredits ?? totalCreditsUsed;
    return (periodCreditUsage.used / total) * 100;
  }, [periodCreditUsage, totalCreditsUsed]);

  if (isLoading) {
    return (
      <div className="w-full">
        <div className="mb-2 flex items-center gap-2">
          <SubTitle text="Total Credit Usage" />
        </div>
        <Skeleton className="block h-8 w-full bg-gray-200" />
      </div>
    );
  }

  if (!periodCreditUsage) {
    return (
      <div className="w-full">
        <div className="mb-2 flex items-center gap-2">
          <SubTitle text="Total Credit Usage" />
        </div>

        <p className="mt-1 text-sm text-gray-600">
          No billing period configured. Please contact support to set up billing tracking.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mb-2 flex items-center gap-2">
        <SubTitle text="Total Credit Usage" />
        {periodCreditUsage.isUsingTrialPackage && <Chip className="bg-teal-100 text-teal-700">Onboarding Package</Chip>}
      </div>

      <p className="mt-1 text-sm text-gray-600">
        Credit usage for the{' '}
        {periodCreditUsage.isUsingTrialPackage
          ? 'onboarding period.'
          : `current billing period across your entire ${t('organisation')}.`}
      </p>

      <div className="relative mt-6">
        <div className="mb-2 flex items-end justify-between">
          <p className="flex h-6 items-end text-xl font-semibold">
            <span
              className={cn(
                !periodCreditUsage.isUsingTrialPackage &&
                  periodCreditUsage.maxCredits &&
                  totalCreditsUsed > periodCreditUsage.maxCredits &&
                  'text-red-600',
                'animate-in fade-in',
              )}
            >
              {new Intl.NumberFormat(i18n.language).format(totalCreditsUsed)}
            </span>

            {periodCreditUsage.isUsingTrialPackage && periodCreditUsage.maxCredits && (
              <TooltipIcon
                side="right"
                triggerClassName="text-teal-500"
                tooltipContent={`The onboarding package has generous limits on usage. Currently this is set to ${new Intl.NumberFormat(
                  'en-US',
                  { notation: 'compact' },
                ).format(
                  periodCreditUsage.maxCredits,
                )} credits. If you feel this is not enough for your use case, reach out to support@wexler.ai.`}
              />
            )}

            {!periodCreditUsage.isUsingTrialPackage && periodCreditUsage.maxCredits && (
              <>
                <span className="inline-flex items-center gap-1.5 pb-0.25 pl-1 text-base text-gray-500">
                  {' '}
                  / {new Intl.NumberFormat('en-US', { notation: 'compact' }).format(periodCreditUsage.maxCredits)} limit
                  <TooltipIcon
                    tooltipContent={`${((totalCreditsUsed / periodCreditUsage.maxCredits) * 100).toFixed(1)}%`}
                  />
                </span>
              </>
            )}
          </p>

          {periodCreditUsage.endDate && (
            <p className="animate-in fade-in mb-1 text-xs text-gray-500">
              usage resets on{' '}
              <span className="font-semibold">
                {new Intl.DateTimeFormat(i18n.language).format(new Date(periodCreditUsage.endDate))}
              </span>
            </p>
          )}
        </div>

        <ProgressBar
          completed={usagePercentage}
          height={'10px'}
          bgColor="var(--color-teal-400)"
          baseBgColor={periodCreditUsage.isUsingTrialPackage ? 'var(--color-teal-100)' : 'var(--color-gray-200)'}
          className="w-full"
          isLabelVisible={false}
          animateOnRender={true}
        />

        <div className="mt-3 flex items-center gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-sm bg-teal-400"></div>
            <span className="text-gray-500">Credits used</span>
          </div>
        </div>
      </div>
    </div>
  );
}
